(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (!root.RealmProductAppearance) {
    root.RealmProductAppearance = api;
    api.mount(root.document, root);
  }
})(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';
  const base = { surface: '#ffffff', text: '#030303', muted: '#4c4f56', divider: '#707076', accent: '#4e5bff', 'primary-bg': '#030303', 'primary-fg': '#ffffff' };
  const parseColor = (value) => {
    const hex = String(value || '').trim().match(/^#([a-f\d]{6})$/i);
    if (hex) return [0, 2, 4].map((index) => parseInt(hex[1].slice(index, index + 2), 16));
    const rgb = String(value || '').trim().match(/^rgba?\(\s*(\d+(?:\.\d+)?)\s*,?\s+(\d+(?:\.\d+)?)\s*,?\s+(\d+(?:\.\d+)?)(?:\s*[,/]\s*(1|1\.0+))?\s*\)$/i);
    if (rgb && rgb.slice(1, 4).every((part) => Number(part) <= 255)) return rgb.slice(1, 4).map(Number);
    return null;
  };
  const luminance = (color) => color.map((value) => { const channel = value / 255; return channel <= .04045 ? channel / 12.92 : ((channel + .055) / 1.055) ** 2.4; }).reduce((total, value, index) => total + value * [0.2126, 0.7152, 0.0722][index], 0);
  const contrast = (a, b) => {
    const first = parseColor(a);
    const second = parseColor(b);
    if (!first || !second) return 0;
    const one = luminance(first);
    const two = luminance(second);
    return (Math.max(one, two) + .05) / (Math.min(one, two) + .05);
  };
  const validatePalette = (raw) => {
    const values = { ...raw };
    const fallback = [];
    if (contrast(values.surface, values.text) < 4.5) { values.surface = base.surface; values.text = base.text; fallback.push('surface/text'); }
    for (const [name, minimum] of [['muted', 4.5], ['divider', 3], ['accent', 4.5]]) {
      if (contrast(values[name], values.surface) < minimum) { values[name] = values.text; fallback.push(name); }
    }
    if (contrast(values['primary-bg'], values['primary-fg']) < 4.5) { values['primary-bg'] = '#4e5bff'; values['primary-fg'] = '#ffffff'; fallback.push('primary pair'); }
    return { values, fallback };
  };
  const mount = (document, window) => {
    let frame = null;
    const ownedArt = new WeakMap();
    const clearPalette = (stage) => {
      const properties = new Set(Object.keys(base).flatMap((name) => [`--realm-pdp-${name}`, `--realm-pdp-raw-${name}`]));
      for (let index = 0; index < stage.style.length; index++) {
        const name = stage.style.item(index);
        if (name.startsWith('--realm-pdp-')) properties.add(name);
      }
      properties.forEach((name) => stage.style.removeProperty(name));
    };
    const clearAppearance = (stage, registry) => {
      stage.querySelectorAll('[data-realm-mounted-eyebrow],[data-realm-mounted-art],[data-realm-appearance-art],[data-realm-appearance-eyebrow],.realm-pdp-art,.realm-pdp-eyebrow').forEach((node) => node.remove());
      if (registry) ownedArt.delete(registry);
      clearPalette(stage);
      delete stage.dataset.realmAppearanceActive;
      Array.from(stage.classList || []).filter((name) => /^(?:realm-appearance-active|realm-product-appearance--.+|realm-pdp--.+)$/.test(name)).forEach((name) => stage.classList.remove(name));
      const notice = registry?.querySelector('[data-realm-appearance-notice]');
      if (notice) notice.textContent = 'Realm base — registry disabled; saved rules are retained. Enable the registry to reactivate the saved rules.';
    };
    const apply = () => {
      frame = null;
      const stage = document.querySelector('#MainContent[data-realm-product-stage]');
      const registry = stage?.querySelector('[data-realm-appearance-registry]');
      if (!stage) return;
      if (!registry || registry.dataset.enabled !== 'true') {
        clearAppearance(stage, registry);
        return;
      }
      stage.dataset.realmAppearanceActive = 'true';
      const incoming = registry.querySelector('[data-realm-appearance-eyebrow]');
      const current = stage.querySelector('[data-realm-mounted-eyebrow]');
      let eyebrow = incoming || current;
      const title = stage.querySelector('.realm-product-title-row');
      stage.querySelectorAll('[data-realm-mounted-eyebrow]').forEach((node) => { if (node !== eyebrow) node.remove(); });
      if (!eyebrow && registry.dataset.eyebrow && title) { eyebrow = document.createElement('span'); eyebrow.className = 'realm-pdp-eyebrow'; }
      if (!registry.dataset.eyebrow) eyebrow?.remove();
      else if (eyebrow && title) { eyebrow.textContent = registry.dataset.eyebrow; eyebrow.hidden = false; eyebrow.dataset.realmMountedEyebrow = ''; title.before(eyebrow); }
      const incomingArt = registry.querySelector('[data-realm-appearance-art]');
      const mountedArt = stage.querySelector('[data-realm-mounted-art]');
      if (incomingArt) ownedArt.set(registry, incomingArt);
      const art = incomingArt || mountedArt || ownedArt.get(registry);
      const gallery = stage.querySelector('.realm-product-gallery');
      stage.querySelectorAll('[data-realm-mounted-art]').forEach((node) => {
        if (node !== art || !registry.dataset.art || registry.dataset.art === 'none' || node.dataset.rule !== registry.dataset.activeRule) node.remove();
      });
      if (art && gallery && registry.dataset.art && registry.dataset.art !== 'none' && art.dataset.rule === registry.dataset.activeRule) {
        art.hidden = false;
        art.dataset.realmMountedArt = '';
        if (art.parentElement !== gallery) gallery.append(art);
      } else art?.remove();
      const image = art?.querySelector('img');
      if (image) {
        const removeFailedArt = () => { if (image.isConnected && image.complete && image.naturalWidth === 0) { ownedArt.delete(registry); art.remove(); } };
        if (!image.dataset.realmAppearanceErrorBound) { image.dataset.realmAppearanceErrorBound = 'true'; image.addEventListener('error', () => { ownedArt.delete(registry); art.remove(); }, { once: true }); }
        removeFailedArt();
      }
      if (registry.dataset.editor !== 'true') {
        clearPalette(stage);
        return;
      }
      const raw = {};
      registry.querySelectorAll('[data-appearance-probe]').forEach((probe) => { raw[probe.dataset.appearanceProbe] = window.getComputedStyle(probe).backgroundColor; });
      const { values, fallback } = validatePalette(raw);
      for (const [name, value] of Object.entries(values)) {
        const property = `--realm-pdp-${name}`;
        if (stage.style.getPropertyValue(property) !== value) stage.style.setProperty(property, value);
      }
      const notice = registry.querySelector('[data-realm-appearance-notice]');
      if (notice) notice.textContent = `${registry.dataset.activeRule ? `Active appearance: ${registry.dataset.activeLabel}.` : 'Realm base — no enabled rule matches this product.'} ${fallback.length ? `Safe fallback: ${fallback.join(', ')}.` : 'Saved colors have valid contrast.'} Product overrides win; first enabled match in its bucket wins. Reset by disabling the registry or choosing Realm base.`;
      registry.dataset.contrastFallback = String(fallback.length > 0);
    };
    const schedule = () => { if (frame === null) frame = window.requestAnimationFrame(apply); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply, { once: true });
    else apply();
    document.addEventListener('shopify:section:load', schedule);
    document.addEventListener('shopify:section:unload', schedule);
    document.addEventListener('shopify:block:select', (event) => {
      if (!event.target.closest?.('[data-realm-appearance-registry]')) return;
      const product = document.querySelector('[data-realm-product]');
      product?.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
    window.addEventListener('pageshow', schedule);
    if (window.MutationObserver) {
      const stage = document.querySelector('#MainContent[data-realm-product-stage]');
      if (stage) {
        const observer = new window.MutationObserver((records) => {
          if (records.some((record) => {
            if (record.type === 'attributes') return record.target === stage || record.target.matches?.('[data-realm-appearance-registry]');
            return [...record.addedNodes, ...record.removedNodes].some((node) => node.matches?.('[data-realm-appearance-registry],[data-realm-appearance-art],[data-realm-appearance-eyebrow],[data-realm-mounted-art],[data-realm-mounted-eyebrow],.realm-pdp-art,.realm-pdp-eyebrow') || node.querySelector?.('[data-realm-appearance-registry]'));
          })) schedule();
        });
        const observe = () => observer.observe(stage, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-enabled', 'class', 'style'] });
        observe();
        window.addEventListener('pagehide', () => { observer.disconnect(); if (frame !== null) window.cancelAnimationFrame(frame); frame = null; });
        window.addEventListener('pageshow', (event) => { if (event.persisted) observe(); });
      }
    }
    if (window.Shopify?.designMode && window.MutationObserver) {
      const observer = new window.MutationObserver((records) => {
        if (records.some((record) => record.target.tagName === 'STYLE' || record.target.parentElement?.tagName === 'STYLE' || record.target === document.documentElement)) schedule();
      });
      observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['style'] });
      let poll = window.setInterval(schedule, 200);
      window.addEventListener('pagehide', () => { window.clearInterval(poll); observer.disconnect(); if (frame !== null) window.cancelAnimationFrame(frame); frame = null; });
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['style'] });
        window.clearInterval(poll);
        poll = window.setInterval(schedule, 200);
      });
    }
  };
  return { parseColor, contrast, validatePalette, mount };
});
